/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { DetailedHTMLProps, HTMLAttributes } from "react";

const Container = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
`;

const Label = styled.span`
  margin-right: 11px;
  font-size: 16px;
  font-weight: medium;
  color: #2f2f2f;
  letter-spacing: 0.15px;
`;

interface SwitchProps {
  toggled: boolean;
  disabled?: boolean;
}

const Switch = styled(motion.div)<SwitchProps>`
  width: 40px;
  height: 18px;
  border-radius: 50px;
  display: flex;
  cursor: pointer;
  position: relative;
  border-color: ${(props) =>
    props.toggled ? "rgba(251, 152, 46, 0.411)" : "#eaeaea"};
  background-color: ${(props) =>
    props.toggled ? "rgba(251, 152, 46, 0.411)" : "#eaeaea"};

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.4;
    `}
`;

const SwitchSelection = styled(motion.div)<SwitchProps>`
  width: 25px;
  height: 25px;
  background-color: ${(props) => (props.toggled ? "#FB972E" : "#979797")};
  border-radius: 25px;
  box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.02);
  position: absolute;
  bottom: -4px;
`;

const variants = {
  on: { right: 0 },
  off: { right: "20px" },
};

interface Props
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  toggled: boolean;
  whenToggled: (toggled: boolean) => void;
  noInfo?: boolean;
  disabled?: boolean;
  texts?: [string, string];
}

export default function StyledSwitch(props: Props): JSX.Element {
  const { toggled, whenToggled, noInfo, disabled, texts } = props;

  return (
    <Container {...props}>
      {!noInfo && (
        <Label>{toggled ? texts?.[1] || "On" : texts?.[0] || "Off"}</Label>
      )}
      <Switch
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;

          whenToggled(!props.toggled);
          e.stopPropagation();
        }}
        toggled={toggled}
      >
        <SwitchSelection
          transition={{ ease: "easeOut", duration: 0.3 }}
          variants={variants}
          animate={toggled ? "on" : "off"}
          toggled={toggled}
        />
      </Switch>
    </Container>
  );
}
